import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreType } from 'reducers/types';
import {
  detectLanguages,
  detectTimeZone,
  getTimeBucket,
  ianaFromTimeZoneText,
  localDayKey,
  localHourIn,
  resolveGreeting,
  ResolvedGreeting,
} from 'utils/localGreeting';

/**
 * A resolved greeting plus the name it was resolved alongside.
 *
 * The name is captured here rather than read from the store at render time so the phrase and
 * the name can never disagree: they are decided together, in one pass, and the component just
 * draws what it is given.
 */
export type DashboardGreeting = ResolvedGreeting & {
  firstName: string | null;
};

/**
 * Resolves the dashboard greeting on the client.
 *
 * Deliberately post-mount: the greeting depends on the browser's time zone and language list,
 * neither of which the server knows, so rendering it during SSR would guarantee a hydration
 * mismatch. It returns `null` until it has something worth showing — the component reserves
 * its height so nothing jumps.
 *
 * It also waits for the user fetch to settle before resolving anything. Resolving early looks
 * broken: the seed falls back to "anon" and the name is still empty, so the dashboard shows a
 * nameless "Hi" for a moment and then visibly swaps *both* the phrase and the name once the
 * real user arrives. Waiting costs a beat of blank space we were already reserving.
 */
export default function useLocalGreeting(): DashboardGreeting | null {
  const savedTimeZoneText = useSelector((state: StoreType) => state.user?.settings?.timeZoneText);
  const userId = useSelector((state: StoreType) => state.user?.id);
  const firstName = useSelector((state: StoreType) => state.user?.firstName);
  const isUserLoading = useSelector((state: StoreType) => state.user?.loading);

  const [greeting, setGreeting] = useState<DashboardGreeting | null>(null);

  useEffect(() => {
    // No user yet, or one being re-fetched: the id and name aren't trustworthy. A re-fetch
    // blanks firstName while keeping the id, so `loading` has to be part of this check.
    // (A failed fetch never clears `loading`, so the greeting stays hidden — which is the
    // right outcome on a dashboard that has no user to greet.)
    if (isUserLoading || !userId) return;

    // The browser's own zone wins; the saved profile time zone is the safety net for
    // browsers that won't report one.
    const timeZone = detectTimeZone() || ianaFromTimeZoneText(savedTimeZoneText);
    const hour = localHourIn(timeZone);

    setGreeting({
      ...resolveGreeting({
        timeZone,
        languages: detectLanguages(),
        hour,
        // Same person, same day, same part of day -> same phrase. Crossing into the
        // afternoon, or coming back tomorrow, deals a different one.
        seed: `${userId}|${localDayKey(timeZone)}|${getTimeBucket(hour)}`,
      }),
      firstName: firstName || null,
    });
  }, [savedTimeZoneText, userId, firstName, isUserLoading]);

  return greeting;
}
